import React, { FC, useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import apiClient from '../api/axiosConfig';
import type { PartyData } from '../types';
import './PartyJoining.scss';

interface PartyJoiningProps {
  onJoined: (data: PartyData) => void;
  onBack: () => void;
  initialPartyId?: string;
}

interface GetPartyResponse {
  name: string;
}

const PartyJoining: FC<PartyJoiningProps> = ({ onJoined, onBack, initialPartyId = '' }) => {
  const [partyId, setPartyId] = useState<string>(initialPartyId.toUpperCase());
  const [username, setUsername] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Keep partyId in sync if initialPartyId changes (e.g. deep-link arrival)
  useEffect(() => {
    if (initialPartyId) setPartyId(initialPartyId.toUpperCase());
  }, [initialPartyId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    if (!partyId.trim() || !username.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const normalizedPartyId = partyId.toLowerCase();
      const response = await apiClient.get<GetPartyResponse>(`/api/party/${normalizedPartyId}`);
      onJoined({
        partyId: normalizedPartyId,
        partyName: response.data.name,
        hostUsername: '',
        username,
      });
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string }>;
      setError(axiosErr.response?.data?.error ?? 'Party not found or invalid ID');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="party-joining">
      <div className="form-container">
        <h2>Join Party</h2>
        {initialPartyId && (
          <p className="join-link-notice">
            You were invited to party <strong>{initialPartyId.toUpperCase()}</strong>. Enter your username to join.
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="partyId">Party ID</label>
            <input
              type="text"
              id="partyId"
              value={partyId}
              onChange={(e) => setPartyId(e.target.value.toUpperCase())}
              placeholder="Enter party ID"
              disabled={loading || !!initialPartyId}
              maxLength={8}
            />
          </div>
          <div className="form-group">
            <label htmlFor="username">Your Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={loading}
              autoFocus={!!initialPartyId}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onBack} disabled={loading}>
              Back
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Joining…' : 'Join Party'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartyJoining;
